import axios from "axios";
import {
  getStoredQuestions,
  saveStoredQuestions,
  getStoredReports,
  saveStoredReports,
  getStoredTests,
  saveStoredTests,
  getStoredAuditLogs,
  saveStoredAuditLogs,
} from "./adminMockData";

const BASE_URL = process.env.REACT_APP_API_URL || "";

const getAuthHeaders = () => {
  const token = localStorage.getItem("access_token");
  return {
    "Accept": "application/json",
    "Content-Type": "application/json",
    ...(token ? { "Authorization": `Bearer ${token}` } : {}),
  };
};

/**
 * Audit log recorder
 */
export const recordAuditLog = ({ action, module, entityType, entityId, beforeValue, afterValue, adminUser }) => {
  try {
    const logs = getStoredAuditLogs();
    const newEntry = {
      id: `AUD-${Date.now()}`,
      admin_user: adminUser || localStorage.getItem("email") || "admin@edukemy.com",
      action,
      module,
      entity_type: entityType,
      entity_id: entityId,
      timestamp: new Date().toISOString(),
      before_value: beforeValue || null,
      after_value: afterValue || null,
    };
    logs.unshift(newEntry);
    saveStoredAuditLogs(logs);
    return newEntry;
  } catch (err) {
    console.error("Failed to record audit log:", err);
  }
};

/* ========================================================
   QUESTION BANK APIS
======================================================== */

export const getQuestionsList = async (filters = {}) => {
  try {
    // Attempt backend endpoint if available
    if (BASE_URL) {
      try {
        const res = await axios.get(`${BASE_URL}admin/questions`, {
          headers: getAuthHeaders(),
          params: filters,
          timeout: 4000,
        });
        if (res.data) return res.data;
      } catch (e) {
        // Fallback to local storage store
      }
    }

    let questions = getStoredQuestions();
    if (filters.subject) {
      questions = questions.filter(q => q.subject.toLowerCase() === filters.subject.toLowerCase());
    }
    if (filters.topic) {
      questions = questions.filter(q => q.topic.toLowerCase().includes(filters.topic.toLowerCase()));
    }
    if (filters.difficulty) {
      questions = questions.filter(q => q.difficulty.toLowerCase() === filters.difficulty.toLowerCase());
    }
    if (filters.pyq_year) {
      questions = questions.filter(q => String(q.pyq_year) === String(filters.pyq_year));
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      questions = questions.filter(q =>
        q.question.toLowerCase().includes(s) ||
        q.id.toLowerCase().includes(s) ||
        (q.topic && q.topic.toLowerCase().includes(s)) ||
        (q.explanation && q.explanation.toLowerCase().includes(s))
      );
    }

    return questions;
  } catch (error) {
    console.error("Error fetching questions list:", error);
    return getStoredQuestions();
  }
};

export const getQuestionById = async (id) => {
  const questions = getStoredQuestions();
  const q = questions.find(item => item.id === id);
  if (!q) throw new Error("Question not found");
  return q;
};

export const createQuestion = async (questionData, adminUser) => {
  const questions = getStoredQuestions();
  const newId = `Q-${Math.floor(1000 + Math.random() * 9000)}`;

  const newQuestion = {
    ...questionData,
    id: newId,
    version: 1,
    status: questionData.status || "Active",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    history: [
      {
        version: 1,
        timestamp: new Date().toISOString(),
        editedBy: adminUser || "admin@edukemy.com",
        changes: "Initial question creation",
      },
    ],
  };

  questions.unshift(newQuestion);
  saveStoredQuestions(questions);

  recordAuditLog({
    action: "Question Created",
    module: "Questions",
    entityType: "Question",
    entityId: newId,
    beforeValue: null,
    afterValue: newQuestion,
    adminUser,
  });

  return newQuestion;
};

export const updateQuestion = async (id, updatedFields, adminUser, reason = "Question content correction") => {
  const questions = getStoredQuestions();
  const index = questions.findIndex(q => q.id === id);
  if (index === -1) throw new Error("Question not found");

  const originalQuestion = { ...questions[index] };
  const nextVersion = (originalQuestion.version || 1) + 1;

  const updatedQuestion = {
    ...originalQuestion,
    ...updatedFields,
    id, // Ensure stable identity
    version: nextVersion,
    updated_at: new Date().toISOString(),
    history: [
      ...(originalQuestion.history || []),
      {
        version: nextVersion,
        timestamp: new Date().toISOString(),
        editedBy: adminUser || "admin@edukemy.com",
        changes: reason,
      },
    ],
  };

  questions[index] = updatedQuestion;
  saveStoredQuestions(questions);

  recordAuditLog({
    action: "Question Updated",
    module: "Questions",
    entityType: "Question",
    entityId: id,
    beforeValue: originalQuestion,
    afterValue: updatedQuestion,
    adminUser,
  });

  return updatedQuestion;
};

export const bulkUploadQuestions = async (questionsArray, adminUser) => {
  const existingQuestions = getStoredQuestions();
  const createdList = [];

  questionsArray.forEach((qData, idx) => {
    const newId = `Q-${Math.floor(1000 + Math.random() * 9000)}-${idx + 1}`;
    const newQuestion = {
      ...qData,
      id: newId,
      version: 1,
      status: "Active",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      history: [
        {
          version: 1,
          timestamp: new Date().toISOString(),
          editedBy: adminUser || "admin@edukemy.com",
          changes: "Bulk upload",
        },
      ],
    };
    createdList.push(newQuestion);
    existingQuestions.unshift(newQuestion);
  });

  saveStoredQuestions(existingQuestions);

  recordAuditLog({
    action: "Bulk Questions Upload",
    module: "Questions",
    entityType: "QuestionBatch",
    entityId: `BATCH-${Date.now()}`,
    beforeValue: null,
    afterValue: { count: createdList.length },
    adminUser,
  });

  return { success: true, count: createdList.length, items: createdList };
};

/* ========================================================
   QUESTION REPORTS APIS
======================================================== */

export const getQuestionReportsList = async (statusFilter = "") => {
  let reports = getStoredReports();
  if (statusFilter && statusFilter !== "All") {
    reports = reports.filter(r => r.status.toLowerCase() === statusFilter.toLowerCase());
  }
  return reports;
};

export const resolveQuestionReport = async (reportId, { action, resolutionNotes, questionUpdates }, adminUser) => {
  const reports = getStoredReports();
  const reportIndex = reports.findIndex(r => r.id === reportId);
  if (reportIndex === -1) throw new Error("Report not found");

  const originalReport = { ...reports[reportIndex] };

  // If question was corrected alongside resolution
  if (questionUpdates && originalReport.question_id) {
    await updateQuestion(
      originalReport.question_id,
      questionUpdates,
      adminUser,
      `Report resolution (${reportId}): ${resolutionNotes || "Corrected question"}`
    );
  }

  const updatedReport = {
    ...originalReport,
    status: action === "reject" ? "Rejected" : "Resolved",
    resolution_notes: resolutionNotes || "",
    resolved_by: adminUser || "admin@edukemy.com",
    updated_at: new Date().toISOString(),
  };

  reports[reportIndex] = updatedReport;
  saveStoredReports(reports);

  recordAuditLog({
    action: action === "reject" ? "Question Report Rejected" : "Question Report Resolved",
    module: "Questions",
    entityType: "QuestionReport",
    entityId: reportId,
    beforeValue: originalReport,
    afterValue: updatedReport,
    adminUser,
  });

  return updatedReport;
};

/* ========================================================
   TEST BUILDER & TEST MANAGEMENT APIS
======================================================== */

export const getTestsList = async (filters = {}) => {
  let tests = getStoredTests();
  if (filters.status && filters.status !== "All") {
    tests = tests.filter(t => t.status.toLowerCase() === filters.status.toLowerCase());
  }
  if (filters.plan && filters.plan !== "All") {
    tests = tests.filter(t => t.plan_tier === filters.plan);
  }
  if (filters.search) {
    const s = filters.search.toLowerCase();
    tests = tests.filter(t =>
      t.title.toLowerCase().includes(s) ||
      t.code.toLowerCase().includes(s) ||
      (t.description && t.description.toLowerCase().includes(s))
    );
  }
  return tests;
};

export const getTestById = async (id) => {
  const tests = getStoredTests();
  const t = tests.find(item => item.id === id);
  if (!t) throw new Error("Test not found");
  return t;
};

export const createTest = async (testData, adminUser) => {
  const tests = getStoredTests();
  const newId = `TEST-${Math.floor(500 + Math.random() * 500)}`;

  const newTest = {
    ...testData,
    id: newId,
    code: testData.code || `TEST-${newId}`,
    status: testData.status || "Draft",
    is_active: testData.status === "Published",
    created_by: adminUser || "admin@edukemy.com",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  tests.unshift(newTest);
  saveStoredTests(tests);

  recordAuditLog({
    action: "Test Created",
    module: "Tests",
    entityType: "Test",
    entityId: newId,
    beforeValue: null,
    afterValue: newTest,
    adminUser,
  });

  return newTest;
};

export const updateTest = async (id, updatedFields, adminUser) => {
  const tests = getStoredTests();
  const index = tests.findIndex(t => t.id === id);
  if (index === -1) throw new Error("Test not found");

  const originalTest = { ...tests[index] };
  const updatedTest = {
    ...originalTest,
    ...updatedFields,
    id,
    updated_at: new Date().toISOString(),
  };

  tests[index] = updatedTest;
  saveStoredTests(tests);

  recordAuditLog({
    action: "Test Updated",
    module: "Tests",
    entityType: "Test",
    entityId: id,
    beforeValue: originalTest,
    afterValue: updatedTest,
    adminUser,
  });

  return updatedTest;
};

export const toggleTestPublish = async (id, shouldPublish, adminUser) => {
  return updateTest(
    id,
    {
      status: shouldPublish ? "Published" : "Draft",
      is_active: shouldPublish,
    },
    adminUser
  );
};

export const toggleTestActive = async (id, isActive, adminUser) => {
  return updateTest(
    id,
    {
      is_active: isActive,
    },
    adminUser
  );
};

/* ========================================================
   AUDIT LOGS APIS
======================================================== */

export const getAuditLogs = async (filters = {}) => {
  let logs = getStoredAuditLogs();
  if (filters.module && filters.module !== "All") {
    logs = logs.filter(l => l.module.toLowerCase() === filters.module.toLowerCase());
  }
  if (filters.search) {
    const s = filters.search.toLowerCase();
    logs = logs.filter(l =>
      l.action.toLowerCase().includes(s) ||
      l.entity_id.toLowerCase().includes(s) ||
      l.admin_user.toLowerCase().includes(s)
    );
  }
  return logs;
};
